/**
 * Grid options specific to the blotter
 */
import { ColDef } from 'ag-grid-community';
import { IndigoAppContextType } from 'app/AppContext';
import Order from 'communication/types/order/Order';
import ColumnPortfolio, {
    BlotterColumn,
    BlotterColumns,
} from './columns/ColumnPortfolio';
import ColumnType from './columns/ColumnType';
import {
    EnhancedColDef,
    removeUserCurrencyColumnNameSuffix,
} from '@instinet-analytics/semantic-ui-components';
import {
    configureColDefTooltip,
    HeaderComponentName,
    SortableHeaderComponentName,
} from '@instinet-analytics/semantic-ui-components';
import { CellClassParams } from 'ag-grid-community/dist/lib/entities/colDef';
import { sideCellRenderer } from './cellRenderers/sideCellRenderer';
import { tickSizeCurrencyCellRenderer } from 'widgets/blotter/cellRenderers/tickSizeCurrencyCellRenderer';
import { currencyCellRenderer } from 'widgets/blotter/cellRenderers/currencyCellRenderer';
import { tickSizeCellRendererx } from 'widgets/blotter/cellRenderers/tickSizeCellRenderer';
import {
    labelFunctionCellExport,
    labelFunctionCellRenderer,
} from 'widgets/blotter/cellRenderers/labelFunctionCellRenderer';
import { colFmtCellRenderer } from 'widgets/blotter/cellRenderers/colFmtCellRenderer';
import { enumMapCellRenderer } from 'widgets/blotter/cellRenderers/enumMapCellRenderer';

const SIDE_COLUMN_WIDTH = 39;

/**
 * blotterColumnDefinitions
 * Converts blotter column metadata to grid column definitions
 */
export function blotterColumnDefinitions(
    columnPortfolio: ColumnPortfolio,
    columns: BlotterColumns,
    context: IndigoAppContextType,
    options?: { disableSorting?: boolean }
) {
    return columns.map<ColDef>((columnName) => {
        const column = columnPortfolio[columnName];

        let width: number = column.width || 0;
        if (columnName === 'side') {
            width = SIDE_COLUMN_WIDTH;
        }

        /**
         * blotterCellStyle
         * Converts blotter column metadata to grid cell styling
         */
        function blotterCellStyle(params: CellClassParams) {
            return {
                textAlign:
                    columnPortfolio[params.colDef.colId as BlotterColumn]
                        .textAlign,
            };
        }

        const ret: EnhancedColDef<unknown> = {
            colId: column.id,
            field: column.dataField,
            headerName: column.name,
            resizable: column.resizable,
            cellStyle: blotterCellStyle,
            width,
            // apply column enumMap formatting if any
            ...(column.enumMap
                ? {
                      cellRenderer: enumMapCellRenderer(context, column),
                  }
                : null),
            // apply column colFmt formatting if any, with the current app context
            ...(column.colFmt
                ? {
                      cellRenderer: colFmtCellRenderer(context, column),
                  }
                : null),
            // apply column labelFunction formatting if any, with the current app context
            ...(column.labelFunction
                ? {
                      cellRenderer: labelFunctionCellRenderer(context, column),
                      getExportValue: labelFunctionCellExport(context, column),
                  }
                : null),

            ...(column.colType === ColumnType.CURRENCY ||
            column.colType === ColumnType.CURRENCYPRICE
                ? {
                      headerName: removeUserCurrencyColumnNameSuffix(
                          column.name
                      ),
                      headerComponentParams: {
                          currencyPrefix: context.currencyFormat.prefix,
                      },
                      headerComponent: options?.disableSorting
                          ? HeaderComponentName
                          : SortableHeaderComponentName,
                      cellRenderer:
                          column.colType === ColumnType.CURRENCYPRICE
                              ? tickSizeCurrencyCellRenderer(context)
                              : currencyCellRenderer(context, column.colFmt),
                  }
                : null),
            ...(column.colType === ColumnType.PRICE
                ? { cellRenderer: tickSizeCellRendererx(context) }
                : null),
            // apply column specific definitions
            ...(columnName === 'side'
                ? { cellRenderer: sideCellRenderer }
                : null),
        };

        configureColDefTooltip(
            context,
            ret,
            column.colType === ColumnType.CURRENCY,
            column.colType === ColumnType.STRING
        );

        return ret;
    });
}

/**
 * getRowDataId
 * Compute grid row id from an Order
 * Necessary for Redux integration https://www.ag-grid.com/react-redux-integration/
 */
export const getRowDataId = (rowData: Order) => rowData.idx.toString();

export const getRowClass = ({ data: rowData }: { data: Order }) => {
    return `order-side-${rowData.side}`;
};
